import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api';
import { useAdmin } from '../AdminContext';
import { useBreakpoint } from '../hooks/useMediaQuery';

const DRAWER_WIDTH_DESKTOP = 420;

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export default function Atlas() {
  const { isMobile } = useBreakpoint();
  const DRAWER_WIDTH = isMobile ? Math.min(DRAWER_WIDTH_DESKTOP, 340) : DRAWER_WIDTH_DESKTOP;
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('atlas_open') === 'true'; } catch { return false; }
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const location = useLocation();
  const { mode, viewAsClient } = useAdmin();

  useEffect(() => {
    try { localStorage.setItem('atlas_open', String(open)); } catch {}
    window.dispatchEvent(new CustomEvent('atlas:toggle', { detail: { open } }));
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg: Message = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      // Send last 10 messages
      const apiMessages = newMessages.slice(-10).map(m => ({
        role: m.role,
        content: m.content,
      }));

      const res = await api.post('/api/atlas/chat', {
        messages: apiMessages,
        mode: viewAsClient ? 'client' : mode,
        client_name: viewAsClient || null,
        current_page: location.pathname,
      });

      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'I ran into an issue — please try again or contact meridianregistry@gmail.com' }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [input, loading, messages, mode, viewAsClient, location.pathname]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const modeBadge = viewAsClient
    ? { label: viewAsClient, bg: 'rgba(0,212,255,0.12)', color: '#00D4FF' }
    : mode === 'admin'
    ? { label: 'Admin', bg: 'rgba(139,92,246,0.12)', color: '#8B5CF6' }
    : { label: 'Demo', bg: 'rgba(245,158,11,0.12)', color: '#F59E0B' };

  const welcomeMessage = `Ask me anything about your outlet data — distribution gaps, match results, market coverage, or how to use the platform.

A few things I can help with:
• What are my top distribution gaps?
• Search the registry for outlets in [city]
• How many outlets are in the registry?
• Show me details for a specific Census ID`;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          position: 'fixed', right: open ? DRAWER_WIDTH + 16 : 16, bottom: 24,
          zIndex: 1001, width: 48, height: 48, borderRadius: '50%',
          background: 'rgba(0,212,255,0.15)', border: '1px solid #00D4FF',
          color: '#00D4FF', cursor: 'pointer', fontSize: '1.1rem',
          fontFamily: "'Space Mono', monospace", fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.3s ease', boxShadow: '0 4px 20px rgba(0,212,255,0.2)',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,212,255,0.25)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,212,255,0.15)'; }}
      >
        {open ? '×' : 'A'}
      </button>

      {/* Drawer */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: DRAWER_WIDTH, zIndex: 1000,
        background: '#0A0F1C', borderLeft: '1px solid rgba(0,212,255,0.15)',
        transform: open ? 'translateX(0)' : `translateX(${DRAWER_WIDTH}px)`,
        transition: 'transform 0.3s ease',
        display: 'flex', flexDirection: 'column',
        fontFamily: "'DM Sans', sans-serif",
      }}>
        {/* Header */}
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid rgba(0,212,255,0.1)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ color: 'white', fontFamily: "'Space Mono', monospace", fontSize: '1rem', fontWeight: 700 }}>Atlas</div>
              <div style={{ color: '#8A9BB5', fontSize: '0.7rem', marginTop: '0.15rem' }}>Meridian Data Assistant</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.6rem', padding: '0.15rem 0.5rem', borderRadius: 4, background: modeBadge.bg, color: modeBadge.color, fontWeight: 600 }}>{modeBadge.label}</span>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: '#8A9BB5', cursor: 'pointer', fontSize: '1.2rem', padding: 0 }}>×</button>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {messages.length === 0 && (
            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '2px 12px 12px 12px', padding: '0.75rem 1rem', maxWidth: '90%', color: '#8A9BB5', fontSize: '0.85rem', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
              {welcomeMessage}
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} style={{
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
            }}>
              <div style={{
                background: msg.role === 'user' ? 'rgba(0,212,255,0.1)' : 'rgba(255,255,255,0.05)',
                border: msg.role === 'user' ? '1px solid rgba(0,212,255,0.2)' : 'none',
                borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '2px 12px 12px 12px',
                padding: '0.6rem 0.9rem',
                color: msg.role === 'user' ? '#E8EFF8' : '#C8D0DE',
                fontSize: '0.85rem', lineHeight: 1.6, whiteSpace: 'pre-wrap',
              }}>
                {msg.content}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '2px 12px 12px 12px', padding: '0.6rem 0.9rem', color: '#00D4FF', fontSize: '0.85rem' }}>
                <span style={{ animation: 'pulse 1.5s ease-in-out infinite' }}>●</span>
                <span style={{ animation: 'pulse 1.5s ease-in-out 0.3s infinite' }}> ●</span>
                <span style={{ animation: 'pulse 1.5s ease-in-out 0.6s infinite' }}> ●</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid rgba(0,212,255,0.1)', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value.slice(0, 500))}
              onKeyDown={handleKeyDown}
              placeholder="Ask Atlas anything..."
              disabled={loading}
              rows={1}
              style={{
                flex: 1, background: '#0F1628', border: '1px solid rgba(0,212,255,0.15)',
                borderRadius: 8, padding: '0.6rem 0.75rem', color: '#E8EFF8',
                fontSize: '0.85rem', fontFamily: "'DM Sans', sans-serif",
                outline: 'none', resize: 'none', maxHeight: 80,
                opacity: loading ? 0.5 : 1,
              }}
              onFocus={e => { e.currentTarget.style.borderColor = '#00D4FF'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'rgba(0,212,255,0.15)'; }}
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              style={{
                background: 'rgba(0,212,255,0.12)', border: '1px solid #00D4FF',
                borderRadius: 8, padding: '0.6rem 0.8rem', cursor: loading || !input.trim() ? 'default' : 'pointer',
                color: '#00D4FF', fontSize: '0.85rem', fontWeight: 700,
                opacity: loading || !input.trim() ? 0.4 : 1,
              }}
            >
              →
            </button>
          </div>
          <div style={{ color: '#5A7090', fontSize: '0.65rem', marginTop: '0.35rem', textAlign: 'right' }}>
            {input.length}/500
          </div>
        </div>
      </div>
    </>
  );
}
